import { useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { SiteFooter } from "../components/SiteFooter";
import { toast } from "sonner";
import { Search, Clock, CheckCircle, XCircle, AlertCircle, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";

const STATUS_MAP = {
  new: { label: "Received", color: "#6B7280", icon: Clock, desc: "Your submission has been received and is under review." },
  booked: { label: "Consultation Booked", color: "#0F3221", icon: CheckCircle, desc: "Your consultation is booked. We'll reach out within 24 hours." },
  pending_approval: { label: "Pending Approval", color: "#D97706", icon: AlertCircle, desc: "Your application is being reviewed. We'll notify you within 48 hours." },
  approved: { label: "Approved", color: "#16A34A", icon: CheckCircle, desc: "You've been approved for direct cultivator access." },
  rejected: { label: "Not Approved", color: "#DC2626", icon: XCircle, desc: "This application was not approved at this time." },
  deposit_paid: { label: "Deposit Received", color: "#0F3221", icon: CheckCircle, desc: "Your concierge deposit is confirmed. Trip planning is in progress." },
  complete: { label: "Complete", color: "#16A34A", icon: CheckCircle, desc: "Your experience is fully confirmed." },
};

const LANE_LABELS = {
  retail: "Retail Inquiry",
  budget: "Budget (Lows)",
  premium: "Premium (KookaMF)",
  boutique: "Boutique (Small Batch)",
  concierge: "Concierge",
};

export default function TrackPage() {
  const [email, setEmail] = useState("");
  const [submissions, setSubmissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const handleSearch = useCallback(async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/track`, { params: { email: email.trim() } });
      setSubmissions(res.data);
      if (res.data.length === 0) toast.info("No submissions found for this email");
    } catch {
      toast.error("Failed to look up status");
    } finally {
      setLoading(false);
    }
  }, [email]);

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-16" style={{ background: "var(--bg-primary)" }} data-testid="track-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-10">
          <a href="/"><img src={LOGO_URL} alt="Green Routes" className="h-10 mx-auto mb-8 logo-blend" /></a>
          <h1 className="text-3xl sm:text-4xl tracking-tight mb-3" style={{ color: "var(--text-primary)" }}>
            Track Your Status
          </h1>
          <p style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
            Enter your email to view the status of your submissions.
          </p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-3 mb-8" data-testid="track-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            data-testid="track-email-input"
            className="flex-1 px-4 py-3 text-sm outline-none"
            style={{ border: "1px solid var(--border)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontFamily: "inherit" }}
          />
          <Button
            type="submit"
            disabled={loading}
            data-testid="track-submit-btn"
            className="px-6 py-3 text-sm font-semibold"
            style={{ background: "var(--accent)", color: "#FFFFFF" }}
          >
            {loading ? "Searching..." : <><Search size={14} className="mr-2" /> Look Up</>}
          </Button>
        </form>

        {submissions !== null && (
          <div data-testid="track-results">
            {submissions.length === 0 ? (
              <div className="text-center py-12" style={{ color: "var(--text-tertiary)" }}>
                <Search size={32} className="mx-auto mb-4 opacity-30" />
                <p className="text-sm">No submissions found for this email address.</p>
                <p className="text-xs mt-2">Make sure you're using the same email you submitted with.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-xs uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>
                  {submissions.length} submission{submissions.length !== 1 ? "s" : ""} found
                </p>
                {submissions.map((sub) => {
                  const status = STATUS_MAP[sub.status] || STATUS_MAP.new;
                  const StatusIcon = status.icon;
                  const isExpanded = expandedId === sub.id;

                  return (
                    <div key={sub.id} style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }} data-testid={`track-submission-${sub.id}`}>
                      <div
                        className="p-5 cursor-pointer flex items-center justify-between"
                        onClick={() => setExpandedId(isExpanded ? null : sub.id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${status.color}10`, border: `1px solid ${status.color}30` }}>
                            <StatusIcon size={18} style={{ color: status.color }} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{status.label}</p>
                            <p className="text-xs mt-0.5" style={{ color: "var(--text-tertiary)" }}>
                              {LANE_LABELS[sub.lane] || sub.lane} &middot; {formatDate(sub.created_at)}
                            </p>
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp size={16} style={{ color: "var(--text-tertiary)" }} /> : <ChevronDown size={16} style={{ color: "var(--text-tertiary)" }} />}
                      </div>

                      {isExpanded && (
                        <div className="px-5 pb-5" style={{ borderTop: "1px solid var(--border)" }}>
                          <p className="text-sm mt-4 mb-4" style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>{status.desc}</p>

                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="p-3" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)" }}>
                              <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Budget</p>
                              <p className="text-sm font-medium mt-1" style={{ color: "var(--text-primary)" }}>{sub.budget?.replace(/_/g, " ") || "—"}</p>
                            </div>
                            <div className="p-3" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border)" }}>
                              <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>Payment</p>
                              <p className="text-sm font-medium mt-1" style={{ color: sub.payment_status === "paid" ? "#16A34A" : "var(--text-primary)" }}>
                                {sub.payment_status === "paid" ? `Paid — $${sub.paid_amount || 0}` : "Pending"}
                              </p>
                            </div>
                          </div>

                          {sub.tags && sub.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {sub.tags.map((tag) => (
                                <span key={tag} className="text-xs px-2 py-1" style={{ background: "rgba(15,50,33,0.05)", border: "1px solid rgba(15,50,33,0.12)", color: "var(--primary)" }}>{tag}</span>
                              ))}
                            </div>
                          )}

                          <p className="text-[10px] mt-2" style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-tertiary)" }}>
                            Ref: {sub.id}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <SiteFooter />
      </div>
    </div>
  );
}
